import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import { supabaseAdmin } from '../config/supabase';
import BlockEditor from '../components/BlockEditor';
import { getHomePlProxyHeaders } from '../utils/proxyUploadHeaders';
import './NewsManagement.css';

const SECTION_LABEL = 'Alarmowanie i ostrzeganie';

async function uploadFileToStorage(file, folder = 'alarm-warning') {
  const uploadUrl = process.env.REACT_APP_UPLOAD_URL?.trim();
  const isAudio = (file.type || '').startsWith('audio/') || /\.wav$/i.test(file.name || '');

  // Ten sam kanał co zdjęcia (upload-proxy / upload.php) — Storage przez proxy często kończy się „Failed to fetch”
  if (uploadUrl) {
    const headers = getHomePlProxyHeaders();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', isAudio ? 'audio' : 'news');
    let response;
    try {
      response = await fetch(uploadUrl, { method: 'POST', headers, body: formData });
    } catch (err) {
      throw new Error(
        isAudio
          ? 'Nie udało się wysłać pliku .wav (Failed to fetch). Zaktualizuj upload.php na serwerze o obsługę type=audio / .wav (patrz upload.php.example).'
          : (err?.message || 'Failed to fetch')
      );
    }
    const text = await response.text();
    if (!response.ok) {
      let msg = `Upload failed: ${response.status}`;
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error) msg = parsed.error;
      } catch {
        // ignore
      }
      throw new Error(msg);
    }
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      const lastBrace = text.lastIndexOf('{');
      if (lastBrace === -1) throw new Error('Nieprawidłowa odpowiedź uploadu');
      data = JSON.parse(text.slice(lastBrace));
    }
    if (!data.url) throw new Error('Upload response missing url');
    return data.url;
  }

  const ext = (file.name && file.name.split('.').pop()) || (isAudio ? 'wav' : 'jpg');
  const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${ext}`;
  // Bucket news-images często whitelisuje tylko image/* — dla wav używamy octet-stream
  const contentType = isAudio ? 'application/octet-stream' : (file.type || 'image/jpeg');
  const { error: uploadError } = await supabaseAdmin.storage
    .from('news-images')
    .upload(fileName, file, { contentType, upsert: false });
  if (uploadError) throw new Error(uploadError.message || 'Błąd przesyłania do Supabase');
  const { data: { publicUrl } } = supabaseAdmin.storage.from('news-images').getPublicUrl(fileName);
  return publicUrl;
}

function blocksToHtml(blocks) {
  if (!blocks || blocks.length === 0) return '';
  return blocks
    .map((block) => {
      if (block.type === 'text') {
        const alignStyle = `style="text-align:${block.align || 'left'}"`;
        const parts = (block.text || '').split('[divider]');
        return parts
          .map((part) => {
            let processed = part
              .replace(/\[sup\]([\s\S]*?)\[\/sup\]/g, '<sup>$1</sup>')
              .replace(/\[sub\]([\s\S]*?)\[\/sub\]/g, '<sub>$1</sub>');
            if (block.bold) processed = `<strong>${processed}</strong>`;
            if (block.italic) processed = `<em>${processed}</em>`;
            if (block.underline) processed = `<u>${processed}</u>`;
            return `<p ${alignStyle}>${processed}</p>`;
          })
          .join('<hr/>');
      }
      if (block.type === 'link') {
        const text = block.text || '';
        const url = block.url || '#';
        const alignStyle = `style="text-align:${block.align || 'left'}"`;
        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
          return `<p ${alignStyle}><a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a></p>`;
        }
        return `<p ${alignStyle}>${text}</p>`;
      }
      if (block.type === 'divider') return '<hr/>';
      return '';
    })
    .join('');
}

const emptyBlock = () => [{ type: 'text', text: '', align: 'left', bold: false, italic: false, underline: false }];

export default function AlarmWarningManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState('');
  const [blocks, setBlocks] = useState(emptyBlock());
  const [active, setActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [audioFiles, setAudioFiles] = useState([]);
  const [existingAudio, setExistingAudio] = useState([]);
  const [removedAudioIds, setRemovedAudioIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseAdmin
        .from('alarm_warning')
        .select(
          'id, title, content, blocks, sort_order, active, created_at, alarm_warning_images(id, image_url, sort_order), alarm_warning_audio(id, audio_url, title, sort_order)'
        )
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error(error);
      toast.error('Błąd ładowania „Alarmowanie i ostrzeganie”');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const resetForm = () => {
    setEditing(null);
    setTitle('');
    setBlocks(emptyBlock());
    setActive(true);
    setSortOrder(0);
    setGalleryFiles([]);
    setExistingImages([]);
    setRemovedImageIds([]);
    setAudioFiles([]);
    setExistingAudio([]);
    setRemovedAudioIds([]);
    setShowForm(false);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setTitle(item.title || '');
    setBlocks(Array.isArray(item.blocks) && item.blocks.length ? item.blocks : emptyBlock());
    setActive(item.active !== false);
    setSortOrder(item.sort_order ?? 0);
    const imgs = [...(item.alarm_warning_images || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const audios = [...(item.alarm_warning_audio || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    setExistingImages(imgs);
    setExistingAudio(audios);
    setRemovedImageIds([]);
    setRemovedAudioIds([]);
    setGalleryFiles([]);
    setAudioFiles([]);
    setShowForm(true);
  };

  const uploadImage = async (file) => uploadFileToStorage(file, 'alarm-warning');

  const handleSave = async (e) => {
    e.preventDefault();
    const cleanTitle = title.trim();
    if (!cleanTitle) {
      toast.error('Podaj tytuł');
      return;
    }
    for (const file of audioFiles) {
      const ok = (file.type || '').includes('wav') || /\.wav$/i.test(file.name || '');
      if (!ok) {
        toast.error('Dozwolone są tylko pliki .wav');
        return;
      }
    }
    setSaving(true);
    try {
      const html = DOMPurify.sanitize(blocksToHtml(blocks));
      const payload = {
        title: cleanTitle,
        content: html,
        blocks,
        active,
        sort_order: Number(sortOrder) || 0,
        updated_at: new Date().toISOString(),
      };

      let topicId = editing?.id;
      if (editing) {
        const { error } = await supabaseAdmin.from('alarm_warning').update(payload).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabaseAdmin.from('alarm_warning').insert(payload).select('id').single();
        if (error) throw error;
        topicId = data.id;
      }

      if (removedImageIds.length > 0) {
        const { error } = await supabaseAdmin.from('alarm_warning_images').delete().in('id', removedImageIds);
        if (error) throw error;
      }
      if (removedAudioIds.length > 0) {
        const { error } = await supabaseAdmin.from('alarm_warning_audio').delete().in('id', removedAudioIds);
        if (error) throw error;
      }

      if (galleryFiles.length > 0) {
        const startOrder = existingImages.filter((img) => !removedImageIds.includes(img.id)).length;
        const rows = [];
        for (let i = 0; i < galleryFiles.length; i += 1) {
          const url = await uploadFileToStorage(galleryFiles[i], 'alarm-warning');
          rows.push({
            alarm_warning_id: topicId,
            image_url: url,
            sort_order: startOrder + i,
          });
        }
        const { error } = await supabaseAdmin.from('alarm_warning_images').insert(rows);
        if (error) throw error;
      }

      if (audioFiles.length > 0) {
        const startOrder = existingAudio.filter((a) => !removedAudioIds.includes(a.id)).length;
        const rows = [];
        for (let i = 0; i < audioFiles.length; i += 1) {
          const file = audioFiles[i];
          const url = await uploadFileToStorage(file, 'alarm-warning-audio');
          rows.push({
            alarm_warning_id: topicId,
            audio_url: url,
            title: (file.name || '').replace(/\.wav$/i, '') || null,
            sort_order: startOrder + i,
          });
        }
        const { error } = await supabaseAdmin.from('alarm_warning_audio').insert(rows);
        if (error) throw error;
      }

      toast.success(editing ? 'Zapisano zmiany' : 'Dodano temat');
      resetForm();
      loadItems();
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Usunąć „${item.title}"?`)) return;
    try {
      const { error } = await supabaseAdmin.from('alarm_warning').delete().eq('id', item.id);
      if (error) throw error;
      toast.success('Usunięto');
      loadItems();
    } catch (error) {
      console.error(error);
      toast.error('Błąd usuwania');
    }
  };

  if (loading && !showForm) {
    return <div className="loading">Ładowanie...</div>;
  }

  if (showForm) {
    return (
      <div className="news-management">
        <div className="page-header">
          <h2>{editing ? `Edycja — ${SECTION_LABEL}` : `Nowy temat — ${SECTION_LABEL}`}</h2>
          <button type="button" className="btn btn-secondary" onClick={resetForm}>
            Wróć do listy
          </button>
        </div>
        <form onSubmit={handleSave} className="card">
          <div className="form-group">
            <label className="form-label">Tytuł *</label>
            <input
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="np. Sygnał ostrzegawczy"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Kolejność</label>
            <input
              type="number"
              className="form-input"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Aktywny (widoczny w aplikacji)
            </label>
          </div>
          <div className="form-group">
            <label className="form-label">Treść</label>
            <BlockEditor blocks={blocks} onBlocksChange={setBlocks} onImageUpload={uploadImage} />
          </div>
          <div className="form-group">
            <label className="form-label">Zdjęcia (na dole ekranu w aplikacji)</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))}
            />
            {existingImages.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
                {existingImages.map((img) => (
                  <div key={img.id}>
                    <img src={img.image_url} alt="" style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8 }} />
                    <button
                      type="button"
                      className="btn btn-danger"
                      style={{ marginTop: 6 }}
                      onClick={() => {
                        setRemovedImageIds((prev) => [...prev, img.id]);
                        setExistingImages((prev) => prev.filter((x) => x.id !== img.id));
                      }}
                    >
                      Usuń
                    </button>
                  </div>
                ))}
              </div>
            )}
            {galleryFiles.length > 0 && (
              <p style={{ marginTop: 8, color: '#666' }}>Nowe zdjęcia: {galleryFiles.length}</p>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Pliki dźwiękowe (.wav)</label>
            <input
              type="file"
              accept=".wav,audio/wav,audio/x-wav"
              multiple
              onChange={(e) => setAudioFiles(Array.from(e.target.files || []))}
            />
            {existingAudio.length > 0 && (
              <ul style={{ marginTop: 12, paddingLeft: 18 }}>
                {existingAudio.map((audio) => (
                  <li key={audio.id} style={{ marginBottom: 8 }}>
                    <a href={audio.audio_url} target="_blank" rel="noreferrer">
                      {audio.title || 'Nagranie'}
                    </a>
                    <button
                      type="button"
                      className="btn btn-danger"
                      style={{ marginLeft: 8 }}
                      onClick={() => {
                        setRemovedAudioIds((prev) => [...prev, audio.id]);
                        setExistingAudio((prev) => prev.filter((x) => x.id !== audio.id));
                      }}
                    >
                      Usuń
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {audioFiles.length > 0 && (
              <p style={{ marginTop: 8, color: '#666' }}>
                Nowe pliki WAV: {audioFiles.map((f) => f.name).join(', ')}
              </p>
            )}
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={saving}>
              Anuluj
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="news-management">
      <div className="page-header">
        <h2>{SECTION_LABEL}</h2>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          Dodaj temat
        </button>
      </div>
      <p style={{ color: '#666', marginBottom: 16 }}>
        Tematy widoczne w aplikacji w OCiZK → {SECTION_LABEL}. Treść, zdjęcia oraz pliki .wav z odtwarzaczem.
      </p>
      {items.length === 0 ? (
        <div className="card">Brak tematów. Dodaj pierwszy.</div>
      ) : (
        <div className="card">
          <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8 }}>Tytuł</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Kolejność</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Audio</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Status</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: 8 }}>{item.title}</td>
                  <td style={{ padding: 8 }}>{item.sort_order}</td>
                  <td style={{ padding: 8 }}>{(item.alarm_warning_audio || []).length}</td>
                  <td style={{ padding: 8 }}>{item.active ? 'Aktywny' : 'Ukryty'}</td>
                  <td style={{ padding: 8 }}>
                    <div className="action-buttons">
                      <button type="button" className="btn btn-secondary" onClick={() => openEdit(item)}>
                        Edytuj
                      </button>
                      <button type="button" className="btn btn-danger" onClick={() => handleDelete(item)}>
                        Usuń
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
